<script setup>
import {
  computed,
  onActivated,
  onBeforeMount,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import glossaryAPI from 'dashboard/api/glossary';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const isSaving = ref(false);
const isSyncing = ref(false);
const glossary = ref(null);

const form = reactive({
  id: null,
  name: '',
  source_language: '',
  target_language: '',
  terms: [],
});

const glossaryId = computed(() => route.params.glossaryId);
const statusLabel = status => {
  const statusMap = {
    pending: t('GLOSSARY_SETTINGS.STATUS.PENDING'),
    synced: t('GLOSSARY_SETTINGS.STATUS.SYNCED'),
    failed: t('GLOSSARY_SETTINGS.STATUS.FAILED'),
  };

  return statusMap[status] || status || '-';
};

const applyGlossaryToForm = resource => {
  form.id = resource?.id || null;
  form.name = resource?.name || '';
  form.source_language = resource?.source_language || '';
  form.target_language = resource?.target_language || '';
  form.terms = (resource?.terms || []).map(term => ({
    source_term: term.source_term || '',
    target_term: term.target_term || '',
    entry_id: term.entry_id || null,
  }));
};

const alertForLocalFirstSave = resource => {
  if (resource?.status === 'failed') {
    useAlert(t('GLOSSARY_SETTINGS.MESSAGES.SAVE_SYNC_FAILED'));
    return;
  }

  useAlert(t('GLOSSARY_SETTINGS.MESSAGES.SAVE_SUCCESS'));
};

const goToList = () => {
  router.push({
    name: 'glossary_list',
    params: { accountId: route.params.accountId },
  });
};

const fetchGlossary = async () => {
  isLoading.value = true;
  try {
    const response = await glossaryAPI.show(glossaryId.value);
    glossary.value = response.data;
    applyGlossaryToForm(response.data);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('GLOSSARY_SETTINGS.MESSAGES.DETAIL_LOAD_FAILED')
    );
    goToList();
  } finally {
    isLoading.value = false;
  }
};

const saveGlossary = async () => {
  if (!form.id) return;

  isSaving.value = true;
  try {
    const response = await glossaryAPI.update(form.id, {
      glossary: {
        name: form.name.trim(),
        source_language: form.source_language.trim(),
        target_language: form.target_language.trim(),
        terms: form.terms
          .filter(term => term.source_term?.trim() && term.target_term?.trim())
          .map(term => ({
            source_term: term.source_term.trim(),
            target_term: term.target_term.trim(),
            entry_id: term.entry_id,
          })),
      },
    });

    glossary.value = response.data;
    applyGlossaryToForm(response.data);
    alertForLocalFirstSave(response.data);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('GLOSSARY_SETTINGS.MESSAGES.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const resyncGlossary = async () => {
  if (!form.id) return;

  isSyncing.value = true;
  try {
    const response = await glossaryAPI.resync(form.id);
    glossary.value = response.data;
    applyGlossaryToForm(response.data);
    useAlert(t('GLOSSARY_SETTINGS.MESSAGES.RESYNC_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('GLOSSARY_SETTINGS.MESSAGES.RESYNC_FAILED')
    );
  } finally {
    isSyncing.value = false;
  }
};

const deleteGlossary = async () => {
  if (!form.id) return;

  isSaving.value = true;
  try {
    await glossaryAPI.delete(form.id);
    useAlert(t('GLOSSARY_SETTINGS.MESSAGES.DELETE_SUCCESS'));
    goToList();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('GLOSSARY_SETTINGS.MESSAGES.DELETE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const addTerm = () => {
  form.terms.push({
    source_term: '',
    target_term: '',
    entry_id: null,
  });
};

const removeTerm = index => {
  form.terms.splice(index, 1);
};

onBeforeMount(() => {
  fetchGlossary();
});

onActivated(() => {
  fetchGlossary();
});

watch(glossaryId, (newGlossaryId, oldGlossaryId) => {
  if (newGlossaryId && newGlossaryId !== oldGlossaryId) {
    fetchGlossary();
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('GLOSSARY_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('GLOSSARY_SETTINGS.DETAIL_TITLE')"
        :description="$t('GLOSSARY_SETTINGS.DETAIL_DESCRIPTION')"
        feature-name="integrations"
      >
        <template #actions>
          <Button
            slate
            icon="i-lucide-chevron-left"
            :label="$t('GLOSSARY_SETTINGS.ACTIONS.BACK_TO_LIST')"
            @click="goToList"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div v-if="glossary" class="grid w-full gap-6">
        <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
          <div
            class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
          >
            <div class="grid gap-4">
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ glossary.name }}
                </h2>
                <div
                  class="mt-2 flex items-center gap-2 text-xs uppercase text-n-slate-11"
                >
                  <span>{{ glossary.source_language }}</span>
                  <span class="i-lucide-arrow-right size-3" />
                  <span>{{ glossary.target_language }}</span>
                </div>
              </div>

              <dl class="grid gap-3 text-sm text-n-slate-11">
                <div class="grid gap-1">
                  <dt class="font-medium text-n-slate-12">
                    {{ $t('GLOSSARY_SETTINGS.FIELDS.SYNC_STATUS') }}
                  </dt>
                  <dd>{{ statusLabel(glossary.status) }}</dd>
                </div>
                <div class="grid gap-1">
                  <dt class="font-medium text-n-slate-12">
                    {{ $t('GLOSSARY_SETTINGS.FIELDS.TENCENT_GLOSSARY_ID') }}
                  </dt>
                  <dd>{{ glossary.tencent_glossary_id || '-' }}</dd>
                </div>
                <div
                  v-if="glossary.last_sync_error"
                  class="grid gap-1 text-n-ruby-11"
                >
                  <dt class="font-medium text-n-ruby-11">
                    {{ $t('GLOSSARY_SETTINGS.FIELDS.LAST_SYNC_ERROR') }}
                  </dt>
                  <dd>{{ glossary.last_sync_error }}</dd>
                </div>
              </dl>

              <p
                v-if="glossary.status !== 'synced'"
                class="text-sm text-n-amber-11"
              >
                {{ $t('GLOSSARY_SETTINGS.MESSAGES.UNAVAILABLE') }}
              </p>
            </div>

            <div class="flex flex-wrap gap-2">
              <Button
                slate
                :label="$t('GLOSSARY_SETTINGS.ACTIONS.RESYNC')"
                :is-loading="isSyncing"
                @click="resyncGlossary"
              />
              <Button
                ruby
                :label="$t('GLOSSARY_SETTINGS.ACTIONS.DELETE')"
                :is-loading="isSaving"
                @click="deleteGlossary"
              />
            </div>
          </div>
        </section>

        <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-5">
          <div class="flex items-center justify-between gap-3">
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ $t('GLOSSARY_SETTINGS.TERMS_TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ $t('GLOSSARY_SETTINGS.TERMS_DESCRIPTION') }}
              </p>
            </div>
            <Button
              slate
              icon="i-lucide-plus"
              :label="$t('GLOSSARY_SETTINGS.ACTIONS.ADD_TERM')"
              @click="addTerm"
            />
          </div>

          <div v-if="form.terms.length" class="mt-5 grid gap-3">
            <div
              v-for="(term, index) in form.terms"
              :key="`${form.id}-${index}`"
              class="grid gap-3 rounded-xl border border-n-weak bg-n-background p-3 md:grid-cols-[minmax(0,1fr),minmax(0,1fr),auto]"
            >
              <input
                v-model="term.source_term"
                type="text"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                :placeholder="$t('GLOSSARY_SETTINGS.FORM.SOURCE_TERM')"
              />
              <input
                v-model="term.target_term"
                type="text"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                :placeholder="$t('GLOSSARY_SETTINGS.FORM.TARGET_TERM')"
              />
              <Button
                ruby
                icon="i-lucide-trash-2"
                :label="$t('GLOSSARY_SETTINGS.ACTIONS.REMOVE_TERM')"
                @click="removeTerm(index)"
              />
            </div>
          </div>

          <div
            v-else
            class="mt-5 rounded-xl border border-dashed border-n-weak bg-n-background px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{ $t('GLOSSARY_SETTINGS.EMPTY_TERMS') }}
          </div>

          <div class="mt-5 flex justify-end">
            <Button
              icon="i-lucide-save"
              :label="$t('GLOSSARY_SETTINGS.ACTIONS.SAVE')"
              :is-loading="isSaving"
              @click="saveGlossary"
            />
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
